import { useEffect, useRef, useState } from "react";
import SignaturePad from "signature_pad";
import "assets/fonts/dancing-script/dancing-script.css";

const PEN_MIN_WIDTH = 1;
const PEN_MAX_WIDTH = 2;
const SHEET_COLOR = "#ffffff";
//250 minimum number of painted pixels to consider the signature drawn
const SIGNATURE_PIXEL_THRESHOLD = 250;

// Typed name, written in the pen layer instead of a drawing
// Same ink as the pen (signature_pad default), so both signatures look alike
const TYPED_NAME_COLOR = "#000000";
const TYPED_NAME_FONT_FAMILY = "'Dancing Script', cursive";
const TYPED_NAME_FONT_WEIGHT = 600;
const TYPED_NAME_MAX_FONT_SIZE = 44;
const TYPED_NAME_MIN_FONT_SIZE = 12;
const TYPED_NAME_SIDE_PADDING = 16;

const getTypedNameFont = (fontSize: number): string =>
    `${TYPED_NAME_FONT_WEIGHT} ${fontSize}px ${TYPED_NAME_FONT_FAMILY}`;

// The canvas is the only source of truth about what will really be sent
const countPaintedPixels = (canvas: HTMLCanvasElement): number => {
    const context = canvas.getContext("2d");

    if (!context) {
        return 0;
    }

    const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
    let paintedPixels = 0;

    for (let index = 0; index < data.length; index += 4) {
        const alpha = data[index + 3];

        if (alpha > 0) {
            paintedPixels++;
        }
    }

    return paintedPixels;
};

/**
 * Owns the two stacked canvases of the signature screen: the one the user draws
 * on and the white sheet behind it. The instruction is HTML text between the two.
 * A typed name is written in the pen layer, so it leaves as the same image as a
 * drawn signature.
 */
export const useSignaturePad = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const underLayerCanvasRef = useRef<HTMLCanvasElement>(null);
    const signaturePadRef = useRef<SignaturePad | null>(null);
    // null while drawing
    const typedNameRef = useRef<string | null>(null);

    const [isEmpty, setIsEmpty] = useState(true);

    useEffect(() => {
        const canvas = canvasRef.current;

        if (!canvas) {
            return;
        }

        const signaturePad = new SignaturePad(canvas, {
            minWidth: PEN_MIN_WIDTH,
            maxWidth: PEN_MAX_WIDTH,
        });
        signaturePadRef.current = signaturePad;
        signaturePad.addEventListener("endStroke", refreshEmptyState);

        // Initial sizing
        resizeCanvases();

        window.addEventListener("resize", resizeCanvases);

        return () => {
            window.removeEventListener("resize", resizeCanvases);
            signaturePad.removeEventListener("endStroke", refreshEmptyState);
            signaturePad.off();
            signaturePadRef.current = null;
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    function resizeCanvases() {
        const canvas = canvasRef.current;
        const underLayerCanvas = underLayerCanvasRef.current;
        const canvasWrapper = canvas?.parentElement;

        if (!canvas || !underLayerCanvas || !canvasWrapper) {
            return;
        }

        const wrapperWidth = canvasWrapper.offsetWidth;
        const wrapperHeight = canvasWrapper.offsetHeight;
        const hasSameSize =
            canvas.width === wrapperWidth && canvas.height === wrapperHeight;

        // Resizing wipes the drawing buffer: never do it for nothing
        if (hasSameSize) {
            return;
        }

        const strokes = signaturePadRef.current?.toData() ?? [];

        canvas.width = wrapperWidth;
        canvas.height = wrapperHeight;
        underLayerCanvas.width = wrapperWidth;
        underLayerCanvas.height = wrapperHeight;

        // Replay the strokes lost with the previous buffer
        if (strokes.length > 0) {
            signaturePadRef.current?.fromData(strokes);
        }

        paintTypedName();
        paintSheet();
        refreshEmptyState();
    }

    function paintSheet() {
        const underLayerCanvas = underLayerCanvasRef.current;
        const context = underLayerCanvas?.getContext("2d");

        if (!underLayerCanvas || !context) {
            return;
        }

        /*
          The sheet is painted in the canvas bitmap instead of being left to the
          page background, because browsers that force a dark theme (Samsung
          Internet "Force Dark", Chrome Android "Auto Dark Theme") invert CSS
          colors but leave canvas content untouched. The background turned black
          while the stroke, drawn in the canvas, stayed black: the signature was
          invisible. Filling here keeps the contrast whatever the color scheme,
          and repaints the layer from scratch at the same time.
        */
        context.fillStyle = SHEET_COLOR;
        context.fillRect(0, 0, underLayerCanvas.width, underLayerCanvas.height);
    }

    function refreshEmptyState() {
        setIsEmpty(signaturePadRef.current?.isEmpty() ?? true);
    }

    function clear() {
        signaturePadRef.current?.clear();
        setIsEmpty(true);
        paintSheet();
    }

    function paintTypedName() {
        const canvas = canvasRef.current;
        const context = canvas?.getContext("2d");
        const typedName = typedNameRef.current?.trim();

        if (!canvas || !context || typedName === undefined) {
            return;
        }

        context.clearRect(0, 0, canvas.width, canvas.height);

        if (!typedName) {
            return;
        }

        const maxTextWidth = canvas.width - 2 * TYPED_NAME_SIDE_PADDING;
        let fontSize = TYPED_NAME_MAX_FONT_SIZE;
        context.font = getTypedNameFont(fontSize);

        // A long name shrinks to stay inside the frame
        while (
            fontSize > TYPED_NAME_MIN_FONT_SIZE &&
            context.measureText(typedName).width > maxTextWidth
        ) {
            fontSize--;
            context.font = getTypedNameFont(fontSize);
        }

        context.fillStyle = TYPED_NAME_COLOR;
        context.textAlign = "center";
        context.textBaseline = "middle";
        context.fillText(
            typedName,
            canvas.width / 2,
            canvas.height / 2,
            maxTextWidth,
        );
    }

    // Resolves once the name is in the canvas, ready to be sent
    async function writeTypedName(name: string) {
        typedNameRef.current = name;
        signaturePadRef.current?.clear();
        // Typing replaces drawing: the pen must not mark over the name
        signaturePadRef.current?.off();
        setIsEmpty(true);

        // Drawn before the font is ready, the name would fall back to a system font
        await document.fonts.load(
            getTypedNameFont(TYPED_NAME_MAX_FONT_SIZE),
            name,
        );
        paintTypedName();
    }

    function resumeDrawing() {
        typedNameRef.current = null;
        signaturePadRef.current?.clear();
        signaturePadRef.current?.on();
        setIsEmpty(true);
    }

    // Checked at click time, so an erased signature cannot leave as a blank
    function isSignatureDrawn() {
        const canvas = canvasRef.current;
        const signaturePad = signaturePadRef.current;

        if (!canvas || !signaturePad || signaturePad.isEmpty()) {
            return false;
        }

        return countPaintedPixels(canvas) >= SIGNATURE_PIXEL_THRESHOLD;
    }

    function getSignatureBlob(): Promise<Blob | null> {
        const canvas = canvasRef.current;

        if (!canvas) {
            return Promise.resolve(null);
        }

        return new Promise((resolve) => {
            canvas.toBlob((blob) => resolve(blob), "image/png");
        });
    }

    return {
        canvasRef,
        underLayerCanvasRef,
        isEmpty,
        clear,
        isSignatureDrawn,
        getSignatureBlob,
        writeTypedName,
        resumeDrawing,
    };
};
